/**
 * Lingala bundle, part C — the warehouse console (`wh`, WP-25). **Draft glossary pending review** (WP-16.7),
 * like parts A and B: nothing here has been agreed by a Lingala speaker yet, and the terms below are the ones
 * to argue about first.
 *
 * Why this namespace was the one to draft. The warehouse is worked **on a yard**, not at a desk: the dock, the pick
 * face and the gate are places where somebody reads a screen with a scanner in one hand, and those are exactly the
 * people least well served by French. The register follows parts A and B — Kinshasa Lingala, which borrows freely
 * from French for anything the ERP names (`stock`, `lot`, `commande`, `fournisseur`, `livraison`, `péremption`), and
 * keeps Lingala for what people actually do (`kotánga` to count, `kopona` to pick, `koyamba` to receive, `kokanga` to
 * close).
 *
 * **Glossary, the terms a reviewer should check first** (each one is used consistently throughout):
 *
 * | English | here | note |
 * |---|---|---|
 * | warehouse | `entrepôt` | as in part B's `Bokambi ya stock`; `magazini` is kept for a *shop's* store |
 * | shelf | `rayon` | the retail word Kinshasa uses; `etanda` was rejected as too general |
 * | gate | `ekuke` | part B already uses it for the plant gate |
 * | goods | `biloko` | |
 * | to count | `kotánga` | the same verb as *to read*, which suits a count made with a scanner |
 * | a read (by a tag or a scanner) | `botángi` | so a tag's read and a person's scan are visibly the same act |
 * | to pick | `kopona` | to choose — what a picker does |
 * | to receive | `koyamba` | |
 * | difference | `bokeseni` | a count's variance |
 * | value | `motuya` | from part B |
 * | closed period | `eleko ekangami` | `eleko` = period, from part B's finance screens |
 * | on hand / free / promised | `ezali` / `polele` / `elakami` | the three numbers of the enquiry screen |
 *
 * Placeholders (`{{name}}`) are part of the contract and are never translated. The English and French bundles stay
 * the source of meaning: where this draft and they disagree, they are right and this is wrong.
 */
import type { Bundle } from "../i18n";

export const lnC: Pick<Bundle, "wh"> = {
  wh: {
    title: "Entrepôt",
    lead: "Ba écran ya mosala oyo ba enregistrement ya stock ya ERPNext epesaka te — kobanda na ekuke oyo biloko ebimaka",
    notInRole: "Console ya entrepôt ezali na mokumba na yo te.",
    notBuilt: "Eteni oyo ezali naino na écran te.",
    sections: {
      gate: "Ekuke ya sécurité",
      inbound: "Dock ya koyamba",
      picking: "Esika ya kopona",
      moves: "Ba mouvement",
      counts: "Botángi ya stock",
      enquiry: "Kotala stock",
      period: "Bokangi ya eleko",
    },
    period: {
      company: "Société",
      upto: "Kokanga tii na",
      state: "Eleko ekangami",
      nothingClosed: "Eloko moko te ekangami. Sanza nyonso ezali naino polele mpo na saisie ya kala.",
      closedUpto: "Ekangami tii na {{date}}, na {{by}}.",
      authRole: "{{role}} akoki kokoma na sima ya bokangi.",
      entries: "Ba écriture",
      span: "Grand livre etamboli",
      reposts: "Ba recalcul",
      perHundred: "Mpo na ba écriture 100",
      repostQueue: "Molongo ya ba recalcul",
      repostHint:
        "ERPNext ezongisaka motuya ya histoire tango moto akomi na sima na yango. « Eleki » ezali libunga te — recalcul ya sima esili kozipa eloko mpe esika wana.",
      noReposts: "Eloko moko te esili kozongisama motuya awa.",
      status: {
        Queued: "na molongo",
        "In Progress": "ezali kotambola",
        Completed: "esili",
        Skipped: "eleki",
        Failed: "ekweyi",
      },
      waiting: "{{count}} ezali naino kozela, ya kala koleka ezali ya {{date}}",
      wouldCover: "Bokangi tii na {{date}} elingaki kozipa",
      entriesCovered: "Ba écriture",
      neverCounted: "Etángamaki naino te",
      onHold: "Ezali kozela contrôle",
      ready: "Bilembo ezali malamu : eleko oyo ekoki kosengama.",
      note: "Eloko nyonso oyo Finance esengeli koyeba",
      ask: "Senga Finance ekanga yango",
      asksNotCloses:
        "Oyo ezali kosenga. Ekangi eloko moko te — kokanga eleko ezongisaka motuya ya mbula mobimba mpe ezali mosala ya Finance.",
      notYours: "Kosenga bokangi ezali na mokumba na yo te.",
      refused: "Bosenga eboyami",
      requested: "Esengami : kokanga tii na {{date}}",
    },
    enquiry: {
      company: "Société",
      place: "Esika",
      everywhere: "Bisika nyonso ya société",
      find: "Luka",
      findHint: "Article, nkombo, code-barres, numéro ya série to lot — koma to scanner",
      look: "Tala",
      clear: "Longola",
      as: {
        item: "Etángami lokola code ya article : {{value}}",
        barcode: "Etángami lokola code-barres : {{value}}",
        serial: "Etángami lokola numéro ya série : {{value}}",
        batch: "Etángami lokola lot : {{value}}",
        search: "Elukami na nkombo : {{value}}",
      },
      items: "Ba article",
      item: "Article",
      onHand: "Ezali",
      reserved: "Elakami",
      available: "Polele",
      incoming: "Ezali koya",
      value: "Motuya",
      oversold: "Ba article {{count}} elakami koleka oyo ezali na rayon",
      oversoldTag: "elakami koleka",
      nothing: "Eloko moko te ezali awa.",
      expiry: "Péremption",
      within: "Na kati ya",
      days: "mikolo {{count}}",
      nothingSoon:
        "Eloko moko te esili na kati ya ntango oyo. Ya liboso ekosila nsima ya mikolo {{days}}, na {{date}}.",
      nothingDated: "Eloko moko te awa ezali na date ya péremption.",
      expiredOn: "esilaki {{date}}",
      inDays: "nsima ya mikolo {{count}}",
      stranded: "Eloko te na rayon",
      strandedHint: "Bisika ezali na zéro nzokande bazali kozela eloko na yango",
      onItsWay: "{{qty}} ezali na nzela",
      promised: "{{qty}} elakami, eloko moko te ezali koya",
      belowReorder: "Na nse ya niveau ya bosombi",
      belowReorderHint: "Ba niveau ya ERP moko — écran ya ba mouvement nde ebimisaka bosenga",
      shortBy: "ezangi {{short}} na {{level}}",
      serials: "Ba numéro ya série oyo eyokani te",
      serialsHint: "Ba carton oyo mikanda na yango mpe rayon eyokani te",
      none: "Eloko ya koyanola te.",
    },
    counts: {
      company: "Société",
      warehouse: "Entrepôt",
      policy:
        "Classe A mikolo {{a}} nyonso, B {{b}} nyonso, C {{c}} nyonso · bokeseni koleka {{pct}}% to {{value}} esengi miso ya moto mosusu",
      due: "Esengeli",
      dueOf: "Ba ligne {{due}} na {{lines}} esengeli kotángama",
      neverCounted: "{{n}} etángamaki naino te",
      openSheet: "Fungola feuille ya botángi",
      linesToCount: "Ba ligne {{n}} ya kotánga",
      nothingCreated:
        "Eloko moko te ekelami tango ozali kotánga : feuille ya botángi ezali ajustement ya stock te. Ajustement ekomami na nsuka, mpe kaka soki eloko ekeseni.",
      batch: "Lot",
      boxes: "Ba carton",
      boxesHint: "scanner to koma numéro ya série mokomoko",
      submit: "Silisa botángi ({{n}} na {{of}})",
      countedBy: "Atángi",
      countedByHint: "moto oyo asalaki botángi",
      allAgreed: "Etángami : ba ligne {{n}}, nyonso eyokani",
      adjusted: "Ebongisami",
      opening: "Ezali kofungola…",
      class: "Classe",
      item: "Article",
      value: "Motuya awa",
      every: "Mbala nyonso",
      lastCounted: "Botángi ya nsuka",
      never: "ata mokolo moko te",
      sheet: "Feuille ya botángi",
      backToPlan: "Zonga na oyo esengeli",
      blindHint:
        "Botángi ya miso ekangami : bakolakisa yo te oyo système ekanisi, mpo botángi oyo emoni eyano ezali kaka kondimisa yango. Koma oyo ezali penza wana.",
      openHint: "Okoki komona ba chiffre oyo ezelami mpo okoki mpe kondima bokeseni.",
      found: "Ezwami",
      expected: "Système",
      difference: "Bokeseni",
      agrees: "eyokani",
      recount: "tánga oyo lisusu",
      beyond: "{{n}} libanda — motuya {{value}}, koleka ndelo",
      within: "{{n}} libanda, na kati ya ndelo",
      serialised: "ba carton oyo eyebani",
      record: "Koma ba ligne {{n}}",
      recording: "Ezali kokoma…",
      close: "Kanga botángi",
      closing: "Ezali kokanga…",
      approverNote: "liloba na ntina ya bokeseni oyo ozali kondima",
      closeHint:
        "Eloko moko te ebongisami tii botángi ekangami. Ligne oyo moto moko te atángi ekangaka yango — mpamba ezali zéro te — mpe bokeseni koleka ndelo esengi moto mosusu, kasi atángi te.",
      cannotClose: "Botángi ekoki naino kokangama te",
      closed: "Etángami mpe ekomami",
      closedDetail: "Ba ligne {{agreeing}} eyokani, {{differing}} ekeseni ; grand livre eningani na {{value}}.",
      approvedBy: "Koleka ndelo, endimami na {{who}} : {{items}}",
    },
    moves: {
      company: "Société",
      source: "Kobanda na",
      target: "Cible mpo na rayon mokomoko",
      destinations: "ba rayon {{n}}",
      noShelfLevels:
        "Rayon moko te ezali na niveau ya bosombi ya ye moko, yango wana cible oyo ya likolo nde esalelami. Tia ba niveau na magazini mokomoko mpe rayon mokomoko ekosenga oyo ya ye.",
      movingWithoutLevels:
        "Ba ligne oyo ezali penza kotambola ezali na niveau ya bosombi te : {{items}}. Ba règle {{rules}} ya société oyo ezali na ba ligne ya malembe oyo ekomi pene ya niveau na yango te.",
      toBuy: "Na nse ya niveau na yango, mpe transfert moko te ekoki kosalisa ({{n}})",
      wantedNone: "Basengi yango, mpe source oyo ezali na yango te ({{n}})",
      wantedNoneHint:
        "Écran oyo etongami kaka na oyo source ezali na yango ekangaka monoko kaka tango makambo ezali mabe koleka : réserve esili, mpe ba magazini oyo ezali kozela ebungi na écran. Ba ligne oyo ezali na esika ya kokende mpe eloko ya kotinda te.",
      wantedBy: "Ba rayon {{n}} esengi yango : {{shelves}}",
      toBuyHint:
        "Source oyo esili etondisamaka na bosombi, kasi na mouvement te — mpe kati ya ba société mibale ya groupe yango ezali boteki. Bosenga ebimi lokola brouillon mpo na mosombi.",
      ask: "Bimisa bosenga",
      asking: "Ezali kobimisa…",
      asked: "{{name}} ebimi lokola brouillon mpo na Bosombi.",
      serialised: "ba carton oyo eyebani",
      inSource: "{{n}} awa",
      asks: "ba rayon esengi {{n}}",
      leaves: "{{n}} ekotikala",
      nothingNeeded: "Rayon nyonso ekomi na cible na yango.",
      shelf: "Rayon",
      has: "Ezali na",
      wants: "Esengi",
      moving: "Kotinda",
      notReached: "Ba rayon {{n}} ekoki kozwama te kobanda awa : {{shelves}}",
      approve: "Ndima ba mouvement {{n}}",
      moving_: "Ezali kotinda…",
      approveHint:
        "Eloko naino etamboli te. Kondima ekomisaka transfert mokomoko soumis — transfert ya brouillon etindi eloko te nzokande emonanaka lokola esali yango.",
      moved: "Ba transfert {{n}} ekomami",
      serials: "Ba numéro ya série {{n}} ekomami",
    },
    picking: {
      queue: "Ya kobima",
      counts: {
        total: "Ba commande {{n}} na biloko ya kokende",
        late: "{{n}} na retard",
        beingPicked: "{{n}} ezali koponama",
        services: "{{n}} na eloko ya kopona te",
      },
      order: "Commande",
      customer: "Kiliya",
      due: "Balingi yango",
      toGo: "Etikali kokende",
      needs: "Esengi",
      start: "Pona",
      resume: "Kende liboso",
      late: "mikolo {{n}} na retard",
      state: { today: "lelo", coming: "ezali koya", noDate: "date te" },
      serials: "ba numéro ya série",
      batches: "ba numéro ya lot",
      plainGoods: "biloko oyo etángamaka",
      serviceOnly: "service — efacturami, eponami te",
      nothingDue: "Eloko moko te ezali kozela kobima.",
      face: "Na esika ya kopona",
      backToQueue: "Zonga na oyo esengeli kobima",
      deliverBy: "esengeli na {{date}}",
      scanFirst:
        "Biloko eponamaka na scanner ; ligne oyo ekomami na maboko ekomami lokola bongo, elongo na ntina",
      typingAllowed: "Société oyo escanner ba picking te ; ba ligne ekomami na maboko",
      listClosed: "liste ekangami",
      askedFor: "{{qty}} {{uom}} esengami",
      shelf: "{{actual}} na rayon · {{free}} polele · {{others}} elakami na ba commande mosusu",
      shelfShort: "rayon ekoki kokokisa elaka ya commande oyo te, ezangi {{n}}",
      fefo: "Zwa liboso :",
      allocationOk: "Lot oyo epesamaki, mpe ezali naino malamu tii livraison :",
      allocationBad: "Lot oyo epesamaki ekokoka te :",
      takeInstead: "Zwa oyo na esika na yango :",
      noBatch: "lot moko te ya article oyo ezali na rayon oyo",
      fefoShort: "{{n}} ezangi na oyo ligne esengi",
      taken: "Ezwami",
      batch: "Lot",
      scanEachBox: "Scanner carton mokomoko (Enter ebakisi yango)",
      scanTheGoods: "Scanner biloko",
      removeSerial: "Longola numéro ya série oyo",
      shortReason: "Soki ezangi, mpo na nini",
      shortReasonHint: "ebebi, etángamaki mabe, ezali na rayon te…",
      record: "Koma ba picking",
      recording: "Ezali kokoma…",
      close: "Kanga liste",
      closeHint:
        "Liste ekangamaka tango ligne nyonso eponami mobimba, to ezangi na ntina. Na nsima bon ekosalema na oyo eponamaki penza, mpe charge ekobima na ekuke.",
      cannotClose: "Liste ekoki naino kokangama te",
      untouched: "Moto moko te asimbi : {{items}}",
      unexplained: "Ezangi kasi ntina ezali te : {{items}}",
      recorded: "Ba ligne {{n}} ekomami, {{short}} ezangi",
      despatch: "Sala bon ya livraison",
      despatching: "Ezali kosala…",
      noteMade: "Bon ya livraison",
      nowTheGate:
        "Charge ekoki sikoyo kobima na ekuke, oyo ekokanisa ba tag na bon oyo.",
    },
    inbound: {
      queue: "Ezali koya",
      sample: "échantillon {{n}}",
      inspectionNote: "liloba (soki olingi)",
      sendReadings: "Tinda ba mesure",
      inspecting: "Ezali kotala…",
      notYourInspection: "Kozwa mokano ya contrôle ezali na mokumba na yo te.",
      accepted: "Endimami",
      rejected: "Eboyami",
      receiptSubmitted: "{{name}} ekoti : biloko ezali na stock.",
      stillWaiting: "Ba contrôle {{n}} ya bon oyo ezali naino kozela.",
      supplierProblem:
        "Eloko moko te ekoti na stock. {{receipt}} etikali brouillon lokola enregistrement ya oyo ekomaki, mpe mosombi akosolola na fournisseur.",
      heldTitle: "Ekangami mpo na contrôle ({{n}})",
      heldState: { waiting: "ezali kozela contrôle", refused: "eboyami", ready: "elengeli" },
      refusedHint: "eloko moko te eyambami ; biloko etikali ya fournisseur mpe mosombi akosolola na ye",
      heldHint:
        "Ba bon oyo ezali ba brouillon, yango nde bokangi : eloko moko te ya kati na yango ezali na stock. Ba mesure nde ekoloba soki ekoti.",
      rejectedNow: "Eboyami",
      counts: {
        total: "Ba commande {{n}} na eloko ya koyamba",
        late: "{{n}} na retard",
        today: "{{n}} esengeli lelo",
        services: "{{n}} oyo dock ekoki koyamba te",
      },
      order: "Commande",
      supplier: "Fournisseur",
      due: "Esengeli",
      pending: "Etikali koya",
      needs: "Esengi",
      open: "Yamba",
      late: "mikolo {{n}} na retard",
      state: { today: "lelo", coming: "ezali koya", noDate: "date te" },
      serials: "ba numéro ya série",
      batches: "ba numéro ya lot",
      inspection: "contrôle",
      plainGoods: "biloko oyo etángamaka",
      serviceOnly: "service — endimami mpe efacturami, eyambami na dock te",
      nothingDue: "Eloko moko te ezali kozela koyambama.",
      dock: "Na dock",
      backToQueue: "Zonga na oyo ezali koya",
      scanFirst:
        "Biloko eyambamaka na scanner ; ligne oyo ekomami na maboko ekomami lokola bongo, elongo na ntina",
      typingAllowed: "Société oyo escanner biloko ya koyamba te ; ba ligne ekomami na maboko",
      allowance: "koyamba koleka endimami tii {{pct}}%",
      noAllowance: "koyamba koleka endimami te",
      youMayOverride: "okoki kondima koleka oyo commande epesi nzela",
      expected: "{{pending}} {{uom}} ezelami naino",
      alreadyIn: "{{n}} esili koyambama",
      counted: "Etángami",
      destination: "Ekokende na",
      scanEachBox: "Scanner carton mokomoko (Enter ebakisi yango)",
      scanTheGoods: "Scanner biloko",
      serialPlaceholder: "numéro ya série oyo ezali na carton",
      codePlaceholder: "code-barres to code ya article",
      serialsRead: "Ba numéro ya série {{n}} etángami mpo na ba unité {{want}}",
      removeSerial: "Longola numéro ya série oyo",
      batchNo: "Lot",
      made: "Esalemaki na",
      expiry: "Esili na",
      shelfLife: "bomoi ya rayon mikolo {{n}} — date ya péremption ekobima na yango",
      willBeHeld:
        "Article oyo etalaka contrôle ({{template}}) ; ekoyambama na quarantaine, na stock te",
      willBeShort: "ezangi {{n}} — oyo etikali ekotikala na commande",
      exact: "kaka oyo commande ezelaki",
      over: "{{n}} koleka oyo commande ezelaki",
      heldForInspection: "ekangami mpo na contrôle",
      recordedAsTyped: "ekomami lokola ekomamaki na maboko",
      notADocksWork: "Ezali na commande oyo, kasi ezali mosala ya dock te",
      why: { notStock: "ezali article ya stock te", alreadyIn: "esili koyambama mobimba" },
      deliveryComplete: "Livraison oyo esili",
      completeHint:
        "Kotia elembo oyo ekomaka oyo commande ezangi naino, na bon mpe na commande, mpo moto alanda yango. Tika yango polele soki eloko mosusu ezali koya.",
      receive: "Yamba",
      receiving: "Ezali koyamba…",
      fixFirst: "Makambo {{n}} ya kobongisa liboso",
      countSomething: "Tánga eloko liboso",
      received: "Eyambami",
      serialsRecorded: "Ba numéro ya série {{n}} ekomami",
      shortRecorded: "Ezangi, mpe ekomami",
      overRecorded: "Endimami koleka oyo commande epesaki nzela, mpe ekomami na nkombo na yo",
    },
    gate: {
      presented: "Oyo elakisami na ekuke",
      presentedHint:
        "Scanner dispatch. Scan yango moko nde eyebisi charge mpe epesi nzela ya kobima, yango wana esengeli ezala ya yo, ya tag te.",
      scanDispatch: "Scanner to koma dispatch, bon ya nzela to numéro ya bolandeli",
      find: "Luka charge",
      looking: "Ezali koluka…",
      releaseAuthority:
        "Scan oyo nde epesi nzela ya kobima — ekomami lokola etángamaki na yo, na ekuke oyo.",
      notADispatch:
        "Wana ezali {{kind}} ({{what}}) — dispatch te oyo ekuke oyo ekoki kotika ebima. Ekuke etikaka ba dispatch ya flotte ; oyo mosusu nyonso ezongaka na bureau.",
      unknownScan: "Eloko moko te ya locataire oyo eyanoli na {{payload}}.",
      reads: "Oyo ezali na kati ya charge",
      readsHint:
        "Ba botángi ya portail, to scan ya loboko mpo na carton mokomoko soki portail ezali te. Botángi nyonso ekomami lokola eloko oyo etángaki yango penza : botángi ya tag ezali temwe ya mouvement, scan ya moto epesaka nzela, mpe moko akitani mosusu te.",
      scanCarton: "Scanner carton",
      addRead: "Bakisa botángi",
      portalOutput: "Oyo portail etángi — tag moko na ligne moko",
      takePortalOutput: "Zwa ba botángi ya portail",
      removeRead: "Longola botángi oyo",
      readers: {
        rfid: "Portail RFID",
        gate: "antenne ya ekuke",
        nfc: "NFC",
        camera: "caméra",
        wedge: "scanner ya loboko",
        ring: "scanner ya mosapi",
        imager: "imageur",
        app: "application",
        barcode: "code-barres",
        qr: "QR",
        manual: "ekomami na maboko",
        other: "mosusu",
      },
      ambientHint: "Etángami moto azalaki wana te : ezali temwe ya mouvement, epesi nzela te.",
      deliberateHint: "Moto ascanné yango na nko.",
      kinds: {
        order: "dispatch",
        tracking_number: "numéro ya bolandeli",
        serial_no: "numéro ya série",
        erp_document: "mokanda ya ERP",
        item: "article",
        rfid_tag: "tag RFID",
        vehicle_plate: "plaque ya veturi",
        place: "esika",
        driver: "chauffeur",
      },
      load: "Charge",
      loadPreview:
        "oyo ekuke oyo emoni tii sikoyo — plateforme nde epesaka eyano ya solo na tango ya kotika ebima",
      nothingPresented: "Scanner dispatch mpo omona oyo elobi ezali komema.",
      line: "Ligne",
      serial: "Numéro ya série",
      tag: "Tag",
      seen: "Emonani",
      notSeen: "emonani naino te",
      noLines: "Dispatch oyo etángi eloko moko te, yango wana ba botángi ezali na eloko ya kotemwe te.",
      release: "Tika charge ebima",
      releasing: "Ezali kotika…",
      notYourRole:
        "Kotika charge ebima esengi ndingisa ya kobimisa (`orders:release`) — mokengeli ya ekuke, dispatcheur to administrateur.",
      refused: "Ekuke eboyi charge oyo",
      missing: "Ba ligne oyo ba botángi emonaki te",
      unexpected: "Etángami, kasi ezali na ligne moko te ya dispatch oyo",
      released: "Etikami ebima",
      releasedDetail:
        "Ba ligne {{witnessed}} na {{lines}} etemwe, ba botángi ya kozongela {{repeats}} etángami.",
      proof: "Elembo",
      releasedBy: "Etikami na",
    },
  },
};
